import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { OrderCreateView } from 'src/sections/orders/view/order-create-view';

import { AuthGuard, RoleBasedGuard } from 'src/auth/guard';

import { AdminRoleEnum } from 'src/types/user';

import { usePathname } from '../hooks';

// ----------------------------------------------------------------------

const IndexPage = lazy(() => import('src/pages/dashboard/dashboard'));
// Media
const MediasPage = lazy(() => import('src/pages/dashboard/medias/list'));
const MediaDetailsPage = lazy(() => import('src/pages/dashboard/medias/details'));
const NewMediaPage = lazy(() => import('src/pages/dashboard/medias/new'));

//Categories
const CategoriesPage = lazy(() => import('src/pages/dashboard/medias/categories'));

//Media Packs
const MediaPacksPage = lazy(() => import('src/pages/dashboard/medias/packs/list'));
const MediaPackNewPage = lazy(() => import('src/pages/dashboard/medias/packs/new'));
const MediaPackDetailsPage = lazy(() => import('src/pages/dashboard/medias/packs/[id]/index'));
const MediaPackEditPage = lazy(() => import('src/pages/dashboard/medias/packs/[id]/edit'));

//Order
const OrdersPage = lazy(() => import('src/pages/dashboard/orders/list'));
const OrderDetailsPage = lazy(() => import('src/pages/dashboard/orders/details'));
const OrderExternalsPage = lazy(() => import('src/pages/dashboard/orders/externals/list'));
const OrderExternalEditPage = lazy(() => import('src/pages/dashboard/orders/externals/edit'));
const OrderExternalPendingPayment = lazy(
  () => import('src/pages/dashboard/orders/externals/pending-payment')
);

//Invoice
const InvoicesPage = lazy(() => import('src/pages/dashboard/invoices/list'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoices/new'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoices/details'));

//Outreach
const OutreachPage = lazy(() => import('src/pages/dashboard/outreach/list'));

//Billing
const PlatformPage = lazy(() => import('src/pages/dashboard/billing/platforms/list'));
const PlatformDetailsPage = lazy(() => import('src/pages/dashboard/billing/platforms/details'));
const NewPlatformPage = lazy(() => import('src/pages/dashboard/billing/platforms/new'));

//Customer
const CustomersPage = lazy(() => import('src/pages/dashboard/customers/list'));
const CustomerDetailsPage = lazy(() => import('src/pages/dashboard/customers/details'));
const NewCustomerPage = lazy(() => import('src/pages/dashboard/customers/new'));
const CustomerImportMediaPage = lazy(() => import('src/pages/dashboard/customers/import-media'));

//Coupons
const CouponsPage = lazy(() => import('src/pages/dashboard/tools/coupons'));

//Calendar
const CalendarPage = lazy(() => import('src/pages/dashboard/tools/calendar'));

//Media Proposals
const MediaProposalsPage = lazy(() => import('src/pages/dashboard/tools/media-proposals/list'));
const MediaProposalCreatePage = lazy(() => import('src/pages/dashboard/tools/media-proposals/new'));

//Profit Management
const ProfitManagementPage = lazy(() => import('src/pages/dashboard/tools/profit-management'));

//Expense Invoices
const ExpenseInvoicesPage = lazy(() => import('src/pages/dashboard/invoices/expenses/list'));
const ExpenseInvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoices/expenses/new'));
const ExpenseInvoiceDetailsPage = lazy(
  () => import('src/pages/dashboard/invoices/expenses/details')
);

//Payment Requests
const PaymentRequestsPage = lazy(() => import('src/pages/dashboard/invoices/payment-requests/list'));

//Admin Users
const AdminUsersPage = lazy(() => import('src/pages/dashboard/admin-users/list'));
const AdminUserCreatePage = lazy(() => import('src/pages/dashboard/admin-users/new'));
const AdminUserDetailsPage = lazy(() => import('src/pages/dashboard/admin-users/details'));
const PendingJournalistsPage = lazy(() => import('src/pages/dashboard/pending-journalists/list'));

//Banners
const BannersPage = lazy(() => import('src/pages/dashboard/banners/list'));
const BannerNewPage = lazy(() => import('src/pages/dashboard/banners/new'));
const BannerDetailsPage = lazy(() => import('src/pages/dashboard/banners/details'));

//GMB Review Packs
const GMBReviewPackCreatePage = lazy(
  () => import('src/pages/dashboard/tools/profit-management/gmb/new')
);
const GMBReviewPackEditPage = lazy(
  () => import('src/pages/dashboard/tools/profit-management/gmb/[id]/edit')
);

//Press Release Packs
const PressReleasePackEditPage = lazy(
  () => import('src/pages/dashboard/tools/profit-management/press-release/[id]/edit')
);

//GMB Booster Packs
const GMBBoosterPackEditPage = lazy(
  () => import('src/pages/dashboard/tools/profit-management/gmb-booster/[id]/edit')
);

//Local Citation Packs
const LocalCitationPackEditPage = lazy(
  () => import('src/pages/dashboard/tools/profit-management/local-citation/[id]/edit')
);

//Mailgun
const MailgunTemplatesPage = lazy(() => import('src/pages/dashboard/tools/mailgun/list'));
const MailgunTemplateNewPage = lazy(() => import('src/pages/dashboard/tools/mailgun/new'));
const MailgunTemplateDetailsPage = lazy(() => import('src/pages/dashboard/tools/mailgun/details'));

//Category Analyzer
const CategoryAnalyzerPage = lazy(() => import('src/pages/dashboard/tools/category-analyzer'));

//Newsletter Ads Requests
const NewsletterAdsRequestsPage = lazy(() => import('src/pages/dashboard/tools/newsletter-ads-requests/list'));
const NewsletterAdsRequestDetailsPage = lazy(() => import('src/pages/dashboard/tools/newsletter-ads-requests/[id]/index'));

//Managed Services
const ManagedServicesPage = lazy(() => import('src/pages/dashboard/managed-services/list'));
const ManagedServiceEditPage = lazy(() => import('src/pages/dashboard/managed-services/edit'));

//Editor Management
const PendingMediasPage = lazy(() => import('src/pages/dashboard/editor-management/pending-medias'));
const EditorNewslettersPage = lazy(() => import('src/pages/dashboard/editor-management/newsletters/list'));
const EditorNewsletterNewPage = lazy(() => import('src/pages/dashboard/editor-management/newsletters/new'));
const EditorNewsletterDetailsPage = lazy(() => import('src/pages/dashboard/editor-management/newsletters/[id]/index'));
const EditorNewsletterEditPage = lazy(() => import('src/pages/dashboard/editor-management/newsletters/[id]/edit'));

//Moderation
const MessagesModerationPage = lazy(() => import('src/pages/dashboard/moderation/messages'));
const BannedWordsPage = lazy(() => import('src/pages/dashboard/moderation/banned-words'));



function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      {
        element: (
          <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
            <IndexPage />
          </RoleBasedGuard>
        ),
        index: true,
      },
      {
        path: 'medias',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                  AdminRoleEnum.EditorManager,
                ]}
              >
                <MediasPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                ]}
              >
                <NewMediaPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'packs',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <MediaPacksPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <MediaPackNewPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <MediaPackDetailsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <MediaPackEditPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                  AdminRoleEnum.EditorManager,
                ]}
              >
                <MediaDetailsPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'categories',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[
                      AdminRoleEnum.Administrator,
                      AdminRoleEnum.Supervisor,
                      AdminRoleEnum.Outreach,
                      AdminRoleEnum.EditorManager,
                    ]}
                  >
                    <CategoriesPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'import-media',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
              >
                <CustomerImportMediaPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },

      {
        path: 'orders',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.EditorManager,
                  AdminRoleEnum.Editor,
                ]}
              >
                <OrdersPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'list',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.EditorManager,
                  AdminRoleEnum.Editor,
                ]}
              >
                <OrdersPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.EditorManager,
                  AdminRoleEnum.Editor,
                ]}
              >
                <OrderDetailsPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'externals',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[
                      AdminRoleEnum.Administrator,
                      AdminRoleEnum.Supervisor,
                      AdminRoleEnum.EditorManager,
                    ]}
                  >
                    <OrderExternalsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[
                      AdminRoleEnum.Administrator,
                      AdminRoleEnum.Supervisor,
                      AdminRoleEnum.EditorManager,
                    ]}
                  >
                    <OrderExternalsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'pending-payments',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[
                      AdminRoleEnum.Administrator,
                      AdminRoleEnum.Supervisor,
                      AdminRoleEnum.EditorManager,
                    ]}
                  >
                    <OrderExternalPendingPayment />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
                  >
                    <OrderCreateView />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
                  >
                    <OrderExternalEditPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'invoices',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Accountant,
                ]}
              >
                <InvoicesPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Accountant,
                ]}
              >
                <InvoiceCreatePage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Accountant,
                ]}
              >
                <InvoiceDetailsPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'expenses',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor, AdminRoleEnum.Accountant]}
                  >
                    <ExpenseInvoicesPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor, AdminRoleEnum.Accountant]}
                  >
                    <ExpenseInvoiceCreatePage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor, AdminRoleEnum.Accountant]}
                  >
                    <ExpenseInvoiceDetailsPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'payment-requests',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
                  >
                    <PaymentRequestsPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'outreach',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                ]}
              >
                <OutreachPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'customers',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
              >
                <CustomersPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'list',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
              >
                <CustomersPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
              >
                <CustomerDetailsPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
              >
                <NewCustomerPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'billing',
        children: [
          {
            path: 'platforms',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
                  >
                    <PlatformPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
                  >
                    <PlatformDetailsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
                  >
                    <NewPlatformPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'tools',
        children: [
          {
            index: true,
            path: 'coupons',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
              >
                <CouponsPage />
              </RoleBasedGuard>
            ),
          },
          {
            index: true,
            path: 'calendar',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
              >
                <CalendarPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'media-proposals',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[
                      AdminRoleEnum.Administrator,
                      AdminRoleEnum.Supervisor,
                      AdminRoleEnum.Outreach,
                    ]}
                  >
                    <MediaProposalsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[
                      AdminRoleEnum.Administrator,
                      AdminRoleEnum.Supervisor,
                      AdminRoleEnum.Outreach,
                    ]}
                  >
                    <MediaProposalCreatePage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'profit-management',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
                  >
                    <ProfitManagementPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'gmb',
                children: [
                  {
                    path: 'new',
                    element: (
                      <RoleBasedGuard
                        allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
                      >
                        <GMBReviewPackCreatePage />
                      </RoleBasedGuard>
                    ),
                  },
                  {
                    path: ':id/edit',
                    element: (
                      <RoleBasedGuard
                        allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
                      >
                        <GMBReviewPackEditPage />
                      </RoleBasedGuard>
                    ),
                  },
                ],
              },
              {
                path: 'press-release',
                children: [
                  {
                    path: ':id/edit',
                    element: (
                      <RoleBasedGuard
                        allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
                      >
                        <PressReleasePackEditPage />
                      </RoleBasedGuard>
                    ),
                  },
                ],
              },
              {
                path: 'gmb-booster',
                children: [
                  {
                    path: ':id/edit',
                    element: (
                      <RoleBasedGuard
                        allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
                      >
                        <GMBBoosterPackEditPage />
                      </RoleBasedGuard>
                    ),
                  },
                ],
              },
              {
                path: 'local-citation',
                children: [
                  {
                    path: ':id/edit',
                    element: (
                      <RoleBasedGuard
                        allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
                      >
                        <LocalCitationPackEditPage />
                      </RoleBasedGuard>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'mailgun',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <MailgunTemplatesPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'templates',
                children: [
                  {
                    index: true,
                    element: (
                      <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                        <MailgunTemplatesPage />
                      </RoleBasedGuard>
                    ),
                  },
                  {
                    path: 'new',
                    element: (
                      <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                        <MailgunTemplateNewPage />
                      </RoleBasedGuard>
                    ),
                  },
                  {
                    path: ':name',
                    element: (
                      <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                        <MailgunTemplateDetailsPage />
                      </RoleBasedGuard>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'category-analyzer',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <CategoryAnalyzerPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'newsletter-ads-requests',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <NewsletterAdsRequestsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <NewsletterAdsRequestDetailsPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'admin-users',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Outreach, AdminRoleEnum.Supervisor]}>
                <AdminUsersPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Outreach, AdminRoleEnum.Supervisor]}>
                <AdminUserCreatePage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Outreach, AdminRoleEnum.Supervisor]}>
                <AdminUserDetailsPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'pending-journalists',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Outreach, AdminRoleEnum.Supervisor]}>
                <PendingJournalistsPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Outreach, AdminRoleEnum.Supervisor]}>
                <AdminUserDetailsPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'banners',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <BannersPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <BannerNewPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <BannerDetailsPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'managed-services',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
              >
                <ManagedServicesPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}
              >
                <ManagedServiceEditPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'editor-management',
        children: [
          {
            path: 'pending-medias',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.EditorManager]}>
                <PendingMediasPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'newsletters',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.EditorManager]}>
                    <EditorNewslettersPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.EditorManager]}>
                    <EditorNewsletterNewPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.EditorManager]}>
                    <EditorNewsletterDetailsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.EditorManager]}>
                    <EditorNewsletterEditPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'moderation',
        children: [
          {
            path: 'messages',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}>
                <MessagesModerationPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'banned-words',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Supervisor]}>
                <BannedWordsPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
    ],
  },
];
